/**
 * PersonView view component.
 * @module components/theme/View/PersonView
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import config from '@plone/volto/registry';
import phoneSVG from '@plone/volto/icons/mobile.svg';
import mailSVG from '@plone/volto/icons/email.svg';
import worldSVG from '@plone/volto/icons/world.svg';
import faxSVG from '@plone/volto/icons/printer.svg';
import phone2SVG from '../../icons/phone.svg';

import Icon from '@plone/volto/components/theme/Icon/Icon';

interface PersonViewProps {
  content: {
    first_name: string;
    last_name?: string;
    description?: string;
    text?: {
      data: string;
    };
    image?: {
      scales?: {
        preview?: {
          download: string;
        };
      };
      download?: string;
    };
    academic_title?: {
      title: string;
    };
    organisational_unit_reference?: {
      title: string;
    };
    department?: string;
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
    contact_website?: string;
    contact_building?: string;
    contact_room?: string;
    office_phone?: string;
    fax?: string;
    address?: string;
    location_reference?: {
      title: string;
    };
  };
}

/**
 * PersonView view component class.
 * @function PersonView
 * @params {object} content Content object.
 * @returns {string} Markup of the component.
 */
const PersonView: React.FC<PersonViewProps> = ({ content }) => {
  const Container = config.getComponent({ name: 'Container' }).component;
  const Image = config.getComponent({ name: 'Image' }).component;

  const profile = {
    imageUrl: content.image ?? null,
    fullName: content.academic_title?.title
      ? content.last_name
        ? `${content.academic_title.title} ${content.first_name} ${content.last_name}`
        : `${content.academic_title.title} ${content.first_name}`
      : content.last_name
        ? `${content.first_name} ${content.last_name}`
        : content.first_name,
    department: content.department ?? null,
    organisationalUnit: content.organisational_unit_reference?.title ?? null,
  };

  const contact = {
    officePhone: content.office_phone ?? null,
    mobilePhone: content.contact_phone ?? null,
    fax: content.fax ?? null,
    email: content.contact_email ?? null,
    website: content.contact_website ?? null,
    contactBuilding: content.contact_building ?? null,
    contactRoom: content.contact_room ?? null,
  };

  return (
    <Container id="page-document" className="view-wrapper person-view">
      <div className="person-profile">
        <header className="profile-header">
          {profile.imageUrl && (
            <Image
              className="profile-image"
              item={content}
              imageField="image"
              alt={profile.fullName}
            />
          )}
          <div className="profile-info">
            <span className="organisational-unit">
              {profile.organisationalUnit}
            </span>
            <h1>{profile.fullName}</h1>
            <div className="affiliations">
              {profile.department && <span>{profile.department}</span>}
            </div>
          </div>
        </header>

        {(contact.officePhone ||
          contact.mobilePhone ||
          contact.fax ||
          contact.email ||
          contact.website) && (
          <div className="contact-wrapper">
            <section aria-labelledby="contact-heading">
              <h2 id="contact-heading">
                <FormattedMessage id="Contact" defaultMessage="Contact" />
              </h2>
              <div className="contact-list">
                {contact.officePhone && (
                  <div className="contact-item">
                    <Icon
                      title={
                        <FormattedMessage id="Phone" defaultMessage="Phone" />
                      }
                      name={phone2SVG}
                      size="36px"
                    />
                    <a
                      href={`tel:${contact.officePhone}`}
                      aria-label="Office phone"
                    >
                      {contact.officePhone}
                    </a>
                  </div>
                )}

                {contact.mobilePhone && (
                  <div className="contact-item">
                    <Icon
                      title={
                        <FormattedMessage id="Phone" defaultMessage="Phone" />
                      }
                      name={phoneSVG}
                      size="36px"
                    />
                    <a
                      href={`tel:${contact.officePhone}`}
                      aria-label="Office phone"
                    >
                      <span aria-label="Mobile phone">
                        {contact.mobilePhone}
                      </span>
                    </a>
                  </div>
                )}

                {contact.fax && (
                  <div className="contact-item">
                    <Icon
                      title={
                        <FormattedMessage id="Phone" defaultMessage="Phone" />
                      }
                      name={faxSVG}
                      size="36px"
                    />
                    <span aria-label="Fax">{contact.fax}</span>
                  </div>
                )}

                {contact.email && (
                  <div className="contact-item">
                    <Icon
                      title={
                        <FormattedMessage id="E-mail" defaultMessage="E-mail" />
                      }
                      name={mailSVG}
                      size="36px"
                    />
                    <a href={`mailto:${contact.email}`} aria-label="Email">
                      {contact.email}
                    </a>
                  </div>
                )}

                {contact.website && (
                  <div className="contact-item">
                    <Icon
                      title={
                        <FormattedMessage id="Site" defaultMessage="Site" />
                      }
                      name={worldSVG}
                      size="36px"
                    />
                    <a href={contact.website} aria-label="Website">
                      {contact.website}
                    </a>
                  </div>
                )}
              </div>
            </section>

            {content?.location_reference && (
              <section aria-labelledby="address-heading">
                <h2 id="address-heading">
                  <FormattedMessage id="Address" defaultMessage="Address" />
                </h2>
                <address>
                  <h3>{content.location_reference.title}</h3>
                  {profile.department && (
                    <p className="department">{profile.department}</p>
                  )}

                  <div className="building-room">
                    {contact.contactBuilding && (
                      <div>
                        <FormattedMessage
                          id="Building"
                          defaultMessage="Building"
                        />{' '}
                        {contact.contactBuilding}
                        {contact.contactRoom && (
                          <span>
                            {' '}
                            /{' '}
                            <FormattedMessage
                              id="Room"
                              defaultMessage="Room"
                            />{' '}
                            {contact.contactRoom}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {content.address && (
                    <p className="address">{content.address}</p>
                  )}
                </address>
              </section>
            )}
          </div>
        )}

        {content.text && (
          <section className="bio-section" aria-labelledby="bio-heading">
            <h2 id="bio-heading">
              <FormattedMessage id="Bio" defaultMessage="Bio" />
            </h2>
            <div className="bio-content">
              <p dangerouslySetInnerHTML={{ __html: content.text.data }} />
            </div>
            <svg
              className="bio-section-border bio-section-border--desktop"
              viewBox="0 0 948 63"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.582031 1H117.744V60.9434L177.688 1H947.416"
                stroke="black"
              />
            </svg>
            <svg
              className="bio-section-border bio-section-border--tablet"
              width="730"
              height="63"
              viewBox="0 0 730 63"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M0 1H56.7997V60.9434L116.766 1H730" stroke="black" />
            </svg>
            <svg
              className="bio-section-border bio-section-border--mobile"
              width="335"
              height="63"
              viewBox="0 0 335 63"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.652344 1H57.4304V60.9434L117.374 1H334.347"
                stroke="black"
              />
            </svg>
          </section>
        )}
      </div>
    </Container>
  );
};

export default PersonView;
